// Office-side panel for the WSHC online-application details collected from the client at report endorsement.
// Read-only by default (copy-paste into the WSHC portal); an Edit toggle lets the office fix a typo. The
// auto/locked fields (from the audit/questionnaire — Employees is auditor-verified) stay read-only here so they
// can never drift from the report. Never shown on the audit report.
import React, { useState } from 'react'

const WSHC_APPLY = 'https://www.tal.sg/wshc/programmes/bizsafe/apply-for-bizsafe/bizsafe-enterprise'
const LOCKED = ['company_name', 'uen', 'application_type', 'address', 'employees']
const designationOf = (c) => c?.designation === 'Others' ? (c?.designation_other || 'Others') : (c?.designation || '')

const lbl = { fontSize: 13, color: 'var(--text2)' }
const val = { fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
function Row({ label, children }) {
  return <div style={{ marginBottom: 8 }}><div style={lbl}>{label}</div><div style={val}>{children || '—'}</div></div>
}
function ContactView({ title, c }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
        <Row label="Name">{c?.name}</Row><Row label="Designation">{designationOf(c)}</Row>
        <Row label="Contact Number">{c?.contact_no}</Row><Row label="Mobile">{c?.mobile}</Row>
        <Row label="Email">{c?.email}</Row>
      </div>
    </div>
  )
}

function ContactEdit({ title, c, onChange }) {
  const set = (k, v) => onChange({ ...c, [k]: v })
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
        <div className="field"><label>Name</label><input value={c?.name || ''} onChange={e => set('name', e.target.value)} /></div>
        <div className="field"><label>Designation</label><input value={designationOf(c)} onChange={e => set('designation', e.target.value)} /></div>
        <div className="field"><label>Contact Number</label><input value={c?.contact_no || ''} onChange={e => set('contact_no', e.target.value)} /></div>
        <div className="field"><label>Mobile</label><input value={c?.mobile || ''} onChange={e => set('mobile', e.target.value)} /></div>
        <div className="field" style={{ gridColumn: '1 / -1' }}><label>Email</label><input value={c?.email || ''} onChange={e => set('email', e.target.value)} /></div>
      </div>
    </div>
  )
}

export default function BizsafeApplicationPanel({ app, onSave, busy }) {
  const [open, setOpen] = useState(false)   // collapsed by default — reference data, not the primary action
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(app || {})
  if (!app) return null

  const startEdit = () => { setDraft(app); setEditing(true) }
  const save = async () => { await onSave(draft); setEditing(false) }
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: open ? 12 : '8px 12px', marginBottom: 12, background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>{open ? '▾' : '▸'}</span> WSHC Application Details
        </button>
        {open && (
          <div style={{ display: 'flex', gap: 6 }}>
            <a href={WSHC_APPLY} target="_blank" rel="noreferrer"><button type="button" className="sm">Open WSHC portal ↗</button></a>
            {!editing && <button type="button" className="sm" onClick={startEdit}>Edit</button>}
          </div>
        )}
      </div>

      {!open ? null : !editing ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            <Row label="Company Name (UEN)">{app.company_name}</Row><Row label="UEN">{app.uen}</Row>
            <Row label="Application Type">{app.application_type}</Row><Row label="Company Type">{app.company_type}</Row>
            <Row label="Is your company an SME?">{app.is_sme}</Row>
            <Row label="Industry Sector">{app.industry_sector}</Row><Row label="bizSAFE Level">{app.level}</Row>
            {app.enterprise_no ? <Row label="Enterprise No.">{app.enterprise_no}</Row> : null}
            <Row label="Total No. of Employees (verified)">{app.employees ?? '—'}</Row>
            <Row label="Company Email">{app.company_email}</Row><Row label="Company Website">{app.company_website}</Row>
          </div>
          <Row label="Address">{app.address}</Row>
          <Row label="Business Description">{app.business_description}</Row>
          <Row label="Key Search Words">{app.key_search_words}</Row>
          <Row label="Certifications">{(app.has_certs && app.has_certs.length) ? app.has_certs.join(', ') : '—'}</Row>
          <Row label="Shared UEN (ticked if yes)">{app.subsidiary ? 'Yes' : 'No'}</Row>
          <ContactView title="Contact Person" c={app.contact} />
          <ContactView title="SGSecure Representative" c={app.sgsecure} />
          <ContactView title="Senior Management" c={app.senior_mgmt} />
          <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 6 }}>Senior Management consent given: {app.senior_consent ? 'Yes' : 'No'}</div>
        </div>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>Auto fields (Company Name, UEN, Application Type, Address, Employees) come from the audit/questionnaire and are not editable here.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            <div className="field"><label>Company Type</label><input value={draft.company_type || ''} onChange={e => set('company_type', e.target.value)} /></div>
            <div className="field"><label>Is your company an SME?</label>
              <select value={draft.is_sme || ''} onChange={e => set('is_sme', e.target.value)}><option value="">—</option><option>Yes</option><option>No</option></select></div>
            <div className="field"><label>Industry Sector</label><input value={draft.industry_sector || ''} onChange={e => set('industry_sector', e.target.value)} /></div>
            <div className="field"><label>bizSAFE Level</label><input value={draft.level || ''} onChange={e => set('level', e.target.value)} /></div>
            <div className="field"><label>Enterprise No.</label><input value={draft.enterprise_no || ''} onChange={e => set('enterprise_no', e.target.value)} /></div>
            <div className="field"><label>Company Email</label><input value={draft.company_email || ''} onChange={e => set('company_email', e.target.value)} /></div>
            <div className="field"><label>Company Website</label><input value={draft.company_website || ''} onChange={e => set('company_website', e.target.value)} /></div>
          </div>
          <div className="field"><label>Business Description</label><textarea rows={3} value={draft.business_description || ''} onChange={e => set('business_description', e.target.value)} /></div>
          <div className="field"><label>Key Search Words</label><textarea rows={2} value={draft.key_search_words || ''} onChange={e => set('key_search_words', e.target.value)} /></div>
          <ContactEdit title="Contact Person" c={draft.contact || {}} onChange={v => set('contact', v)} />
          <ContactEdit title="SGSecure Representative" c={draft.sgsecure || {}} onChange={v => set('sgsecure', v)} />
          <ContactEdit title="Senior Management" c={draft.senior_mgmt || {}} onChange={v => set('senior_mgmt', v)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" className="primary sm" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>
            <button type="button" className="sm" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
